import { useState, useCallback, useMemo } from 'react';
import { useAction, useMutation } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { IGeneratedImage, TAspectRatio } from '@/@types/avatars';

interface IUseAvatarGenerationArgs {
  selectedPersonId: Id<'referencePersons'> | null;
  prompt: string;
  selectedRatio: TAspectRatio;
  imageCount: 1 | 2 | 3 | 4;
  attachedImage: IGeneratedImage | null;
  setAttachedImage: (img: IGeneratedImage | null) => void;
  setPrompt: (p: string) => void;
}

export function useAvatarGeneration({
  selectedPersonId,
  prompt,
  selectedRatio,
  imageCount,
  attachedImage,
  setAttachedImage,
  setPrompt,
}: IUseAvatarGenerationArgs) {
  const navigate = useNavigate();

  const generateImages = useAction(api.ai.avatarGeneration.generateAvatarImages);
  const generatePromptAction = useAction(api.ai.avatarGeneration.generatePrompt);
  const editImageAction = useAction(api.ai.avatarGeneration.editImageWithChat);
  const createFolder = useMutation(api.avatarFolders.createFolder);
  const saveAvatar = useMutation(api.generatedAvatars.saveAvatar);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<IGeneratedImage[]>([]);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isImproving, setIsImproving] = useState(false);

  // Save dialog state
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<Id<'avatarFolders'> | null>(null);
  const [newFolderTitle, setNewFolderTitle] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedImages = useMemo(
    () => generatedImages.filter(img => selectedImageIds.has(img.storageId)),
    [generatedImages, selectedImageIds]
  );

  const handleGenerate = useCallback(async () => {
    if (!selectedPersonId || !prompt.trim()) return;
    setIsGenerating(true);
    setError(null);

    try {
      if (attachedImage) {
        const result = await editImageAction({
          imageStorageId: attachedImage.storageId,
          editPrompt: prompt.trim(),
          referencePersonId: selectedPersonId,
          aspectRatio: selectedRatio,
        });
        if (result.success && result.editedImage) {
          setGeneratedImages(prev => {
            const index = prev.findIndex(img => img.storageId === attachedImage.storageId);
            const editedWithRatio = {
              ...result.editedImage!,
              aspectRatio: attachedImage.aspectRatio || selectedRatio,
            };
            if (index !== -1) {
              const updated = [...prev];
              updated[index] = editedWithRatio;
              return updated;
            }
            return [...prev, editedWithRatio];
          });
          setAttachedImage(null);
        } else {
          setError(result.error || 'Failed to edit image');
        }
      } else {
        const result = await generateImages({
          referencePersonId: selectedPersonId,
          prompt: prompt.trim(),
          count: imageCount,
          aspectRatio: selectedRatio,
        });
        if (result.success && result.images) {
          const imagesWithRatio: IGeneratedImage[] = result.images.map(
            (img: { storageId: Id<'_storage'>; url: string; aspectRatio?: TAspectRatio }) => ({
              ...img,
              aspectRatio: img.aspectRatio || selectedRatio,
            })
          );
          setGeneratedImages(prev => [...prev, ...imagesWithRatio]);
        } else {
          setError(result.error || 'Failed to generate images');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsGenerating(false);
    }
  }, [
    selectedPersonId,
    prompt,
    imageCount,
    selectedRatio,
    attachedImage,
    generateImages,
    editImageAction,
    setAttachedImage,
  ]);

  const handleImprove = useCallback(async () => {
    if (!selectedPersonId || !prompt.trim()) return;
    setIsImproving(true);
    setError(null);
    try {
      const result = await generatePromptAction({
        referencePersonId: selectedPersonId,
        userMessage: `Improve this prompt for avatar generation: "${prompt.trim()}"`,
      });
      if (result.success && result.response) {
        setPrompt(result.response);
      } else {
        setError(result.error || 'Failed to improve prompt');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsImproving(false);
    }
  }, [selectedPersonId, prompt, generatePromptAction, setPrompt]);

  const handleToggleSelect = useCallback((storageId: string) => {
    setSelectedImageIds(prev => {
      const next = new Set(prev);
      if (next.has(storageId)) next.delete(storageId);
      else next.add(storageId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedImageIds(new Set(generatedImages.map(img => img.storageId)));
  }, [generatedImages]);

  const handleDeselectAll = useCallback(() => {
    setSelectedImageIds(new Set());
  }, []);

  const handleDownload = useCallback(async (image: IGeneratedImage) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avatar-${image.storageId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download image');
    }
  }, []);

  const handleEdit = useCallback(
    (image: IGeneratedImage) => {
      setAttachedImage(image);
      setPrompt('');
    },
    [setAttachedImage, setPrompt]
  );

  const handleDelete = useCallback((storageId: string) => {
    setGeneratedImages(prev => prev.filter(img => img.storageId !== storageId));
    setSelectedImageIds(prev => {
      const next = new Set(prev);
      next.delete(storageId);
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(() => {
    setGeneratedImages(prev => prev.filter(img => !selectedImageIds.has(img.storageId)));
    setSelectedImageIds(new Set());
  }, [selectedImageIds]);

  const handleBulkDownload = useCallback(async () => {
    const imgs = generatedImages.filter(img => selectedImageIds.has(img.storageId));
    for (const image of imgs) {
      await handleDownload(image);
    }
  }, [generatedImages, selectedImageIds, handleDownload]);

  const handleSave = useCallback(() => {
    if (selectedImageIds.size === 0) return;
    setIsSaveDialogOpen(true);
  }, [selectedImageIds]);

  const handleSaveConfirm = useCallback(async () => {
    if (selectedImageIds.size === 0 || !selectedPersonId) return;
    setIsSaving(true);
    setError(null);

    try {
      let folderId = selectedFolderId;
      if (!folderId && newFolderTitle.trim()) {
        const folderResult = await createFolder({ title: newFolderTitle.trim() });
        if (folderResult.success && folderResult.folderId) {
          folderId = folderResult.folderId;
        } else {
          setError(folderResult.error || 'Failed to create folder');
          setIsSaving(false);
          return;
        }
      }
      if (!folderId) {
        setError('Please select or create a folder');
        setIsSaving(false);
        return;
      }

      const imagesToSave = generatedImages.filter(img => selectedImageIds.has(img.storageId));
      for (let i = 0; i < imagesToSave.length; i++) {
        const image = imagesToSave[i];
        const result = await saveAvatar({
          folderId,
          referencePersonId: selectedPersonId,
          storageId: image.storageId,
          prompt: prompt.trim(),
          aspectRatio: selectedRatio,
          isReadyToUse: false,
          isDefault: i === 0 ? isDefault : false,
        });
        if (!result.success) {
          setError(result.error || 'Failed to save avatar');
          setIsSaving(false);
          return;
        }
      }

      setIsSaveDialogOpen(false);
      navigate('/ai-avatar/my-avatars');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedImageIds,
    selectedPersonId,
    selectedFolderId,
    newFolderTitle,
    createFolder,
    saveAvatar,
    generatedImages,
    prompt,
    selectedRatio,
    isDefault,
    navigate,
  ]);

  return {
    isGenerating,
    generatedImages,
    selectedImageIds,
    error,
    isImproving,
    isSaveDialogOpen,
    setIsSaveDialogOpen,
    selectedFolderId,
    setSelectedFolderId,
    newFolderTitle,
    setNewFolderTitle,
    isDefault,
    setIsDefault,
    isSaving,
    selectedImages,
    handleGenerate,
    handleImprove,
    handleToggleSelect,
    handleSelectAll,
    handleDeselectAll,
    handleDownload,
    handleEdit,
    handleDelete,
    handleBulkDelete,
    handleBulkDownload,
    handleSave,
    handleSaveConfirm,
  };
}
